import { useMemo } from "react";
import { useLingui } from "@lingui/react/macro";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { allUnits, withCurrentUnit } from "@/lib/units/unit-options";
import type { UnitFamily, UnitFamilyGroup } from "@/types/unit";

interface UnitSelectProps {
  value: string;
  onChange: (value: string) => void;
  /** The families `GET /units` returned, from `useUnits()`. */
  groups: UnitFamilyGroup[];
  /**
   * Offer exactly these units, flat. Omitted offers every unit in `groups`,
   * grouped by family — right for an ingredient, which defines its own family
   * rather than inheriting one.
   */
  units?: string[];
  label: string;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * A unit picker (T-603). Units used to be typed by hand, which is how a recipe
 * ends up costed in "kilo" and "kilos" as if they were different things.
 *
 * A native select rather than the Radix one: this sits inside dialogs that are
 * already portalled, and a plain select is the control a keyboard and a screen
 * reader both already understand.
 */
export function UnitSelect({
  value,
  onChange,
  groups,
  units,
  label,
  id,
  placeholder,
  disabled,
  className,
}: UnitSelectProps) {
  const { t } = useLingui();

  const familyLabels: Record<UnitFamily, string> = {
    weight: t`Weight`,
    volume: t`Volume`,
    count: t`Count`,
    container: t`Bought by the container`,
  };

  // A value the API does not offer still has to be selectable, or opening an
  // ingredient saved before this existed would blank its unit on save.
  const visibleGroups = useMemo((): { family: UnitFamily | null; units: string[] }[] => {
    if (units) return [{ family: null, units: withCurrentUnit(units, value) }];
    const known = allUnits(groups);
    const legacy = withCurrentUnit(known, value).filter((unit) => !known.includes(unit));
    return [
      ...(legacy.length > 0 ? [{ family: null, units: legacy }] : []),
      ...groups.map((group) => ({ family: group.family, units: group.units })),
    ];
  }, [groups, units, value]);

  const isEmpty = visibleGroups.every((group) => group.units.length === 0);

  return (
    <div className="relative">
      <select
        id={id}
        aria-label={label}
        value={value}
        disabled={disabled || isEmpty}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          "h-10 w-full appearance-none rounded-lg border border-(--color-border) bg-(--color-surface-content) px-3 pr-10 text-sm text-(--color-text-primary) outline-none focus:border-(--color-ring) disabled:cursor-not-allowed disabled:opacity-60",
          className,
        )}
      >
        <option value="">{placeholder ?? t`Select a unit...`}</option>
        {visibleGroups.map((group) =>
          group.family === null ? (
            group.units.map((unit) => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))
          ) : (
            <optgroup key={group.family} label={familyLabels[group.family]}>
              {group.units.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </optgroup>
          ),
        )}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-(--color-text-secondary)" />
    </div>
  );
}
